import { useLocale } from '@iris/i18n/react';
import { useAuth } from '@iris/ui/auth/auth-provider';
import { RouteFallback } from '@iris/ui/components/app-screens';
import { BrandMark } from '@iris/ui/components/brand-mark';
import { LanguageSwitch } from '@iris/ui/components/language-switch';
import { OfflineBanner } from '@iris/ui/components/offline-banner';
import { Button } from '@iris/ui/components/ui/button';
import { cn } from '@iris/ui/lib/utils';
import { LogOut } from 'lucide-react';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

const NAV = [
  { to: '/', label: 'ops.nav.dashboard', end: true },
  { to: '/merchants', label: 'ops.nav.shops' },
  { to: '/customers', label: 'ops.nav.customers' },
  { to: '/alerts', label: 'ops.nav.alerts' },
  { to: '/config', label: 'ops.nav.config' },
  { to: '/audit', label: 'ops.nav.audit' },
  { to: '/test-data', label: 'ops.nav.testData' },
] as const;

/**
 * App frame: brand, navigation once signed in, language switch and sign-out. Desktop only (min 1024 px, T4 §1.3).
 * The brand name comes from one key (brand.name).
 */
export function Layout() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { changeLocale, status, signOut } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = `${t('brand.name')} · ${t('ops.title')}`;
  }, [t, locale]);

  return (
    <div className="flex min-h-dvh flex-col bg-neutral-50">
      <OfflineBanner message={t('common.offline')} />
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex w-full min-w-[1024px] max-w-[1440px] items-center justify-between gap-4 px-4 py-3">
          <div className="flex min-w-0 items-center gap-5">
            <span className="flex shrink-0 items-center gap-2 whitespace-nowrap text-lg font-semibold text-primary">
              <BrandMark className="h-7 w-7" />
              {t('brand.name')} <span className="font-normal text-muted-foreground">{t('ops.title')}</span>
            </span>
            {status === 'authenticated' ? (
              <nav aria-label={t('ops.nav.label')} className="flex min-w-0 gap-0.5 overflow-x-auto">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={'end' in item}
                    className={({ isActive }) =>
                      cn(
                        'whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:text-foreground',
                      )
                    }
                  >
                    {t(item.label)}
                  </NavLink>
                ))}
              </nav>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitch
              value={locale}
              onChange={changeLocale}
              label={t('common.language.label')}
              names={{ fr: t('common.language.fr'), ar: t('common.language.ar') }}
            />
            {status === 'authenticated' ? (
              <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                <LogOut aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
                {t('auth.account.logout')}
              </Button>
            ) : null}
          </div>
        </div>
      </header>
      <main className="mx-auto w-full min-w-[1024px] max-w-[1440px] flex-1 px-8 py-8">
        <Suspense fallback={<RouteFallback label={t('common.loading')} />}>
          <div
            key={pathname}
            className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300 motion-reduce:animate-none"
          >
            <Outlet />
          </div>
        </Suspense>
      </main>
    </div>
  );
}
